import { type ReactNode, useEffect, useMemo, useState } from "react";
import { CmsEmptyState } from "../../../../../components/ResearchCms";
import { Button } from "@legal-500/legal500-design-system";
import {
  applyBulkChoice,
  bulkChoiceOffered,
  changeCount,
  clickRowChoice,
  firmControl,
  previewOrgTotals,
  replaceAllDisabledReason,
  replacesExistingItem,
  restorableControls,
  restoreFirmControls,
  withFirmControl,
  type UndoSnapshot,
} from "../../utils/bpaImportItemControl";
import {
  ALL_KINDS,
  firmKey,
  type ClashChoice,
  type ImportKind,
  type ImportPageState,
  type ImportPreviewOrg,
} from "../../utils/bpaImport";
import { ColouredWords } from "./ColouredWords";
import { ImportCheckbox } from "./controls/ImportCheckbox";
import { ImportTable, type ImportTableColumn } from "./controls/ImportTable";
import { SegmentedToggle } from "./controls/SegmentedToggle";
import { ImportSection } from "./ImportSection";
import { KIND_LABEL, REPLACE_NEEDS_DELETE_REASON } from "./importText";
import shared from "./importShared.module.css";
import styles from "./OrganisationsSection.module.css";

function orgName(org: ImportPreviewOrg): string {
  return org.noFirm ? "No firm" : (org.companyName ?? "Unnamed organisation");
}

type OrgRow = { id: string; org: ImportPreviewOrg };

type OrganisationsSectionProps = {
  open: boolean;
  focusToken: number;
  state: ImportPageState;
  loading: boolean;
  reloading?: boolean;
  error: string | null;
  orgs: ImportPreviewOrg[];
  clashCount: number;
  comparingKey: string | null;
  canReplaceTargetContent?: boolean;
  onRetry: () => void;
  onToggle: (key: string) => void;
  onToggleAll: (include: boolean) => void;
  onChoose: (key: string, choice: ClashChoice) => void;
  onCompare: (key: string, trigger?: HTMLElement) => void;
  registerCompareButton?: (key: string, element: HTMLButtonElement | null) => void;
  onStateChange?: (next: ImportPageState) => void;
  undoKey?: string;
  hint?: string | null;
};

type UndoEntry = UndoSnapshot & { scope: "all" | string };

function countCell(org: ImportPreviewOrg, kind: ImportKind, state: ImportPageState): ReactNode {
  if (!state.kinds.includes(kind)) return <span className={shared.muted}>—</span>;
  const count = org.importCounts[kind];
  return count === 0 ? <span className={shared.muted}>—</span> : count;
}

function nameCell(org: ImportPreviewOrg): ReactNode {
  if (!org.noFirm) return orgName(org);
  return (
    <>
      {orgName(org)}
      <span className={styles.sub}>Lawyer rankings not linked to a firm</span>
    </>
  );
}

function ChoiceCell({
  org,
  state,
  onChoose,
  onCompare,
  registerCompareButton,
  canReplaceTargetContent,
  undo,
  onUndo,
}: {
  org: ImportPreviewOrg;
  state: ImportPageState;
  onChoose: (key: string, choice: ClashChoice) => void;
  onCompare: OrganisationsSectionProps["onCompare"];
  registerCompareButton: OrganisationsSectionProps["registerCompareButton"];
  canReplaceTargetContent: boolean;
  undo: UndoEntry | null;
  onUndo: () => void;
}) {
  const key = firmKey(org);
  if (!org.clash) return <span className={shared.muted}>—</span>;
  const name = orgName(org);
  const changes = changeCount(org, firmControl(state, key));
  const replaceBlocked = !canReplaceTargetContent && replacesExistingItem(org);
  return (
    <div className={styles.choice} data-row-click="ignore">
      <SegmentedToggle<ClashChoice>
        size="small"
        name={`clash-${key}`}
        ariaLabel={`Whose content to keep for ${name}`}
        value={state.choices[key] ?? "keep_target"}
        disabled={state.unticked.includes(key)}
        options={[
          {
            value: "keep_target",
            text: "Keep target's",
            ariaLabel: `Keep target's for ${name}`,
          },
          {
            value: "take_source",
            text: "Use source's",
            ariaLabel: `Use source's for ${name}`,
            disabled: replaceBlocked,
          },
        ]}
        onChange={choice => onChoose(key, choice)}
      />
      {replaceBlocked && <span className={styles.sub}>{REPLACE_NEEDS_DELETE_REASON}</span>}
      <span className={styles.changes}>
        {changes > 0 && (
          <>
            <button
              type="button"
              className={styles.compare}
              data-compare-trigger=""
              aria-label={`${changes === 1 ? "1 change" : `${changes} changes`} for ${name}, open Compare`}
              onClick={event => onCompare(key, event.currentTarget)}
            >
              {changes === 1 ? "1 change" : `${changes} changes`}
            </button>
            <span className={styles.separator} aria-hidden="true">
              {" · "}
            </span>
          </>
        )}
        <button
          ref={element => registerCompareButton?.(key, element)}
          type="button"
          className={styles.compare}
          data-compare-trigger=""
          aria-label={`Compare ${name}`}
          onClick={event => onCompare(key, event.currentTarget)}
        >
          Compare
        </button>
      </span>
      {undo && (
        <button
          type="button"
          className={styles.compare}
          aria-label={`Undo reset for ${name}`}
          onClick={onUndo}
        >
          Undo
        </button>
      )}
    </div>
  );
}

const DIM_AFTER_MS = 300;

function useDimmedAfterDelay(reloading: boolean): boolean {
  const [dimmed, setDimmed] = useState(false);
  useEffect(() => {
    if (!reloading) {
      setDimmed(false);
      return;
    }
    const timer = setTimeout(() => setDimmed(true), DIM_AFTER_MS);
    return () => clearTimeout(timer);
  }, [reloading]);
  return dimmed;
}

/** Returns `value`, but keeps the last value it returned for as long as `hold` is true. */
function useHeldWhile<T>(value: T, hold: boolean): T {
  const [held, setHeld] = useState(value);
  if (!hold && held !== value) setHeld(value);
  return hold ? held : value;
}

export function OrganisationsSection(props: OrganisationsSectionProps) {
  const { open, focusToken, state, onToggle, onToggleAll } = props;
  const [sectionMoving, setSectionMoving] = useState(false);
  const [recordedUndo, setUndo] = useState<UndoEntry | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [undoKey, setUndoKey] = useState(props.undoKey);
  if (undoKey !== props.undoKey) {
    setUndoKey(props.undoKey);
    setUndo(null);
    setAnnouncement("");
  }
  const live = useMemo(
    () => ({
      loading: props.loading,
      reloading: Boolean(props.reloading),
      error: props.error,
      orgs: props.orgs,
    }),
    [props.loading, props.reloading, props.error, props.orgs],
  );
  const shown = useHeldWhile(live, sectionMoving);
  const { orgs } = shown;
  const undo =
    recordedUndo && Object.keys(restorableControls(state, recordedUndo, orgs)).length > 0
      ? recordedUndo
      : null;
  const reloading = shown.reloading && !shown.loading && orgs.length > 0;
  const dimmed = useDimmedAfterDelay(reloading);
  const allIncluded = orgs.every(org => !state.unticked.includes(firmKey(org)));
  const noneIncluded = orgs.every(org => state.unticked.includes(firmKey(org)));

  const canDelete = props.canReplaceTargetContent ?? true;
  const bulkReason = replaceAllDisabledReason(orgs, canDelete);

  function chooseForRow(key: string, choice: ClashChoice) {
    if (!props.onStateChange) {
      props.onChoose(key, choice);
      return;
    }
    const result = clickRowChoice(firmControl(state, key), choice);
    props.onStateChange(withFirmControl(state, key, result.control));
    setUndo(
      result.undo
        ? { scope: key, before: { [key]: result.undo }, after: { [key]: result.control } }
        : null,
    );
    const firm = orgs.find(org => firmKey(org) === key);
    setAnnouncement(
      result.undo && firm ? `${orgName(firm)}: changes reset. Undo is available.` : "",
    );
  }

  function chooseForAll(choice: ClashChoice) {
    const result = applyBulkChoice(state, orgs, choice);
    props.onStateChange?.(result.state);
    setUndo(
      result.undo
        ? {
            scope: "all",
            before: result.undo,
            after: Object.fromEntries(
              Object.keys(result.undo).map(key => [key, firmControl(result.state, key)]),
            ),
          }
        : null,
    );
    setAnnouncement(result.undo ? "All firms: changes reset. Undo is available." : "");
  }

  function undoLast() {
    if (!undo) return;
    props.onStateChange?.(restoreFirmControls(state, restorableControls(state, undo, orgs)));
    setUndo(null);
    setAnnouncement("Changes restored.");
  }

  const columns: ImportTableColumn<OrgRow>[] = [
    {
      id: "tick",
      select: true,
      header: (
        <ImportCheckbox
          aria-label="Select all organisations"
          checked={allIncluded}
          indeterminate={!allIncluded && !noneIncluded}
          onChange={() => onToggleAll(!allIncluded)}
        />
      ),
      cell: row => (
        <ImportCheckbox
          aria-label={`Include ${orgName(row.org)}`}
          checked={!state.unticked.includes(row.id)}
          onChange={() => onToggle(row.id)}
        />
      ),
    },
    { id: "name", header: "Organisation", cell: row => nameCell(row.org) },
    ...ALL_KINDS.map(kind => ({
      id: kind,
      header: KIND_LABEL[kind],
      align: "right" as const,
      cell: (row: OrgRow) => countCell(row.org, kind, state),
    })),
    {
      id: "choice",
      header: (
        <>
          <span className={styles.existing} data-side="existing">
            Target
          </span>{" "}
          content
        </>
      ),
      width: "320px",
      cell: row => (
        <ChoiceCell
          org={row.org}
          state={state}
          onChoose={chooseForRow}
          onCompare={props.onCompare}
          registerCompareButton={props.registerCompareButton}
          canReplaceTargetContent={canDelete}
          undo={undo?.scope === firmKey(row.org) ? undo : null}
          onUndo={undoLast}
        />
      ),
    },
  ];

  const rows: OrgRow[] = orgs.map(org => ({ id: firmKey(org), org }));

  return (
    <ImportSection
      number={3}
      title="Organisations"
      description={
        !open ? (
          "Choose a practice area first."
        ) : props.clashCount > 0 ? (
          <>
            <ColouredWords
              text={`${props.clashCount} ${props.clashCount === 1 ? "already has" : "already have"} content in the target. Choose for each whether to keep the target's or use the source's.`}
            />
            {props.hint && ` ${props.hint}`}
          </>
        ) : (
          (props.hint ?? undefined)
        )
      }
      descriptionKey={`${open}:${props.clashCount}:${props.hint ?? ""}`}
      status={open ? "open" : "locked"}
      valid={open && previewOrgTotals(orgs, state).organisations > 0}
      last
      focusToken={focusToken}
      onMovingChange={setSectionMoving}
    >
      <span className={shared.srOnly} role="log">
        {announcement}
      </span>
      {shown.loading && <CmsEmptyState loading message="Loading organisations" />}
      {shown.error && (
        <div className={shared.errorBox} role="alert">
          <span>{shown.error}</span>
          <Button variant="outlined" color="default" size="small" onClick={props.onRetry}>
            Retry
          </Button>
        </div>
      )}
      {!shown.loading && !shown.error && orgs.length === 0 && (
        <p className={styles.empty}>
          {state.kinds.length === 0
            ? "Choose at least one kind of content to import."
            : "No organisations have content of the chosen kinds in the practice area."}
        </p>
      )}
      {!shown.loading && !shown.error && bulkChoiceOffered(orgs) && (
        <div className={styles.bulk}>
          <button
            type="button"
            className={styles.bulkButton}
            onClick={() => chooseForAll("keep_target")}
          >
            Keep target's for all
          </button>
          <button
            type="button"
            className={styles.bulkButton}
            disabled={bulkReason !== null}
            onClick={() => chooseForAll("take_source")}
          >
            Use source's for all
          </button>
          {bulkReason && <span className={styles.sub}>{bulkReason}</span>}
          {undo?.scope === "all" && (
            <button
              type="button"
              className={styles.compare}
              aria-label="Undo reset for all firms"
              onClick={undoLast}
            >
              Undo
            </button>
          )}
        </div>
      )}
      {!shown.loading && !shown.error && orgs.length > 0 && (
        <div
          className={styles.reloadCue}
          aria-busy={reloading}
          data-dimmed={dimmed ? "true" : undefined}
        >
          <ImportTable
            ariaLabel="Organisations to import"
            columns={columns}
            rows={rows}
            highlightedRowId={props.comparingKey}
            onRowClick={row => onToggle(row.id)}
          />
        </div>
      )}
    </ImportSection>
  );
}
